import { fetchOrgs } from '../api/userApi';
import { Organization } from '../interfaces/User';
import { ORG_INFO_SESSION_STORAGE, ORG_SESSION_STORAGE } from '../shared/constants/constants';
import React, { createContext, useEffect, useState } from 'react';

interface OrganizationContextProps {
	orgTree: Organization[] | null;
	selectedOrg: Organization | null;
	setSelectedAccount: (org: Organization) => void;
}

export const OrganizationContext = createContext<OrganizationContextProps>({
	orgTree: null,
	selectedOrg: null,
	setSelectedAccount: () => null,
});

export const OrganizationContextProvider = ({ children }: { children: React.ReactNode }) => {
	const [orgTree, setOrgTree] = useState<Organization[] | null>(null);
	const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);

	const changeAttributeName = (orgArray: Organization[]): Organization[] => {
		return orgArray.map(org => {
			const { nestedContexts, ...rest } = org;
			const updatedOrg: Organization = {
				...rest,
				...(nestedContexts && nestedContexts.length > 0 ? { nestedAccounts: changeAttributeName(nestedContexts) } : {}),
			};
			return updatedOrg;
		});
	};

	const findOrgById = (orgArray: Organization[], id: string | null): Organization | null => {
		if (!id) {
			return null;
		}

		for (const org of orgArray) {
			if (org.id === id) {
				return org;
			}

			if (org.nestedAccounts) {
				const result = findOrgById(org.nestedAccounts, id);
				if (result) {
					return result;
				}
			}
		}

		return null;
	};

	useEffect(() => {
		fetchOrgs().then((organizations: Organization[]) => {
			const rootOrg = changeAttributeName(organizations);
			setOrgTree(rootOrg);

			const selectedOrgId = sessionStorage.getItem(ORG_SESSION_STORAGE);
			setSelectedOrg(findOrgById(rootOrg, selectedOrgId));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const setSelectedAccount = (org: Organization) => {
		setSelectedOrg(org);
		sessionStorage.setItem(ORG_SESSION_STORAGE, org.id);
		sessionStorage.setItem(ORG_INFO_SESSION_STORAGE, JSON.stringify(org));
	};

	return (
		<OrganizationContext.Provider value={{ orgTree, selectedOrg, setSelectedAccount }}>
			{children}
		</OrganizationContext.Provider>
	);
};
